'use client'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE, SectionHead, useReveal } from '@/components/page-kit'
import { PAYROLL_STAGES } from './data'

/* The six stages set as a statement: ordinal, stage, a dotted leader, and the
   description carried out to a common right edge — the way a payslip or a
   filing sets its line items. The shared template renders these as six
   identical chips in a 3-column grid.

   The block closes on a totals rule and "Every single cycle.", which is a
   verbatim fragment of `longDescription`, so the recurrence is stated in the
   product's own words rather than a line I wrote. */

export function PayrollCycle() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The cycle"
          title="Automate complex salary structures, statutory requirements, and tax calculations."
        />

        <div ref={list.ref} className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {PAYROLL_STAGES.map((s, i) => (
            <div
              key={s.name}
              className={cn(
                'flex flex-col gap-1 border-b py-4 sm:py-5',
                'lg:flex-row lg:items-baseline lg:gap-4',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(8px)',
                transition: `opacity 480ms ${EASE} ${i * 70}ms, transform 480ms ${EASE} ${i * 70}ms`,
              }}
            >
              <div className="flex items-baseline gap-4">
                <span className="w-6 shrink-0 text-[11px] tabular-nums text-muted-foreground/60">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-heading whitespace-nowrap text-base font-normal leading-snug text-foreground sm:text-lg">
                  {s.name}
                </h3>
              </div>

              {/* The leader: what makes the row read as a line item rather than
                  a list bullet. Hidden below lg, where the description wraps
                  under the stage name instead. */}
              <span
                aria-hidden
                className="hidden flex-1 translate-y-[-4px] border-b border-dotted border-border lg:block"
              />

              <p className="pl-10 text-[13px] leading-snug text-muted-foreground sm:text-sm lg:pl-0 lg:text-right">
                {s.description}
              </p>
            </div>
          ))}

          {/* The totals line. */}
          <div
            className="flex items-baseline justify-between gap-4 pt-5"
            style={{
              opacity: list.shown ? 1 : 0,
              transition: `opacity 600ms ${EASE} ${PAYROLL_STAGES.length * 70 + 120}ms`,
            }}
          >
            <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Repeats
            </span>
            <span className="font-heading text-base font-normal text-emerald-600 sm:text-lg dark:text-primary-active">
              Every single cycle.
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
